import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import InputAdornment from '@mui/material/InputAdornment';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import { orderBy } from 'lodash-es';
import { useMatch } from '../../contexts/MatchContext';
import * as api from '../../api';
import { getFlagSrc } from '../../utils/scoring';

function ColumnFilter({ field, options, filters, setFilters }) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const selected = filters[field]; // undefined = all selected, Set = specific selection

  const isAllSelected = !selected;
  const isActive = !!selected;

  const handleToggle = (value) => {
    const current = selected || new Set(options.map((o) => o.value));
    const next = new Set(current);
    if (next.has(value)) {
      next.delete(value);
    } else {
      next.add(value);
    }
    // If all are selected, clear the filter
    if (next.size === options.length) {
      setFilters((prev) => { const n = { ...prev }; delete n[field]; return n; });
    } else {
      setFilters((prev) => ({ ...prev, [field]: next }));
    }
  };

  const handleSelectAll = () => {
    setFilters((prev) => { const n = { ...prev }; delete n[field]; return n; });
  };

  const handleSelectNone = () => {
    setFilters((prev) => ({ ...prev, [field]: new Set() }));
  };

  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => { e.stopPropagation(); setAnchorEl(e.currentTarget); }}
        sx={{ p: 0.25, color: isActive ? 'primary.main' : 'action.disabled' }}
      >
        <FilterListIcon sx={{ fontSize: 16 }} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        slotProps={{ paper: { sx: { maxHeight: 350, minWidth: 180 } } }}
      >
        <MenuItem dense onClick={handleSelectAll}>
          <Checkbox size="small" checked={isAllSelected} sx={{ p: 0, mr: 1 }} />
          <ListItemText primary={t('common.selectAll')} primaryTypographyProps={{ variant: 'body2' }} />
        </MenuItem>
        <MenuItem dense onClick={handleSelectNone}>
          <Checkbox size="small" checked={selected?.size === 0} sx={{ p: 0, mr: 1 }} />
          <ListItemText primary={t('common.selectNone')} primaryTypographyProps={{ variant: 'body2' }} />
        </MenuItem>
        <Divider />
        {options.map((opt) => {
          const checked = isAllSelected || (selected && selected.has(opt.value));
          return (
            <MenuItem key={opt.value} dense onClick={() => handleToggle(opt.value)}>
              <Checkbox size="small" checked={checked} sx={{ p: 0, mr: 1 }} />
              <ListItemText primary={opt.label} primaryTypographyProps={{ variant: 'body2' }} />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}

export default function ShootersList() {
  const { t } = useTranslation();
  const { match } = useMatch();
  const [shooters, setShooters] = useState(null);
  const [sortField, setSortField] = useState('squad');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filters, setFilters] = useState({});
  const [searchText, setSearchText] = useState('');
  const [selectedShooter, setSelectedShooter] = useState(null);

  useEffect(() => {
    if (!match) return;
    window.scrollTo(0, 0);

    api.getShooters(match.id).then((res) => {
      setShooters((res.data || []).filter((s) => !s.cancelledParticipation));
    });
  }, [match?.id]);

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const toDivisionKey = (division) => {
    const map = {
      'standard': 'standard', 'production': 'production', 'open': 'open',
      'production-optics': 'productionOptics', 'optics': 'optics', 'classic': 'classic',
      'revolver': 'revolver', 'pcc-optics': 'pccOptics', 'pcc-iron': 'pccIron',
      'sg-open': 'sgOpen', 'sg-modified': 'sgModified', 'sg-standard': 'sgStandard',
      'sg-standard-manual': 'sgStandardManual',
    };
    return map[division] || division;
  };

  const toCategoryKey = (category) => {
    const map = {
      'regular': 'regular', 'senior': 'senior', 'super-senior': 'superSenior',
      'grand-senior': 'grandSenior', 'lady-senior': 'ladySenior', 'junior': 'junior',
      'super-junior': 'superJunior', 'grand-junior': 'grandJunior', 'lady-junior': 'ladyJunior',
      'lady-super-junior': 'ladySuperJunior', 'lady-grand-junior': 'ladyGrandJunior', 'lady': 'lady',
    };
    return map[category] || category;
  };

  const getSquadName = (squadId) => {
    if (!squadId) return '';
    const squad = (match?.squads || []).find((sq) => sq.id === squadId);
    return squad ? squad.title : '';
  };

  const showTeam = (match?.teams || []).length > 0;
  const showIcs = match?.icsEnabled;

  const uniqueValues = useMemo(() => {
    if (!shooters) return {};
    const squads = [...new Set(shooters.map((s) => s.squad).filter(Boolean))]
      .map((id) => ({ value: id, label: getSquadName(id) }))
      .sort((a, b) => a.label.localeCompare(b.label));
    const divisions = [...new Set(shooters.map((s) => s.division).filter(Boolean))].sort()
      .map((v) => ({ value: v, label: t(`divisions.${toDivisionKey(v)}`, { defaultValue: v }) }));
    const categories = [...new Set(shooters.map((s) => s.category).filter(Boolean))].sort()
      .map((v) => ({ value: v, label: t(`categories.${toCategoryKey(v)}`, { defaultValue: v }) }));
    const powerFactors = [...new Set(shooters.map((s) => s['power-factor']).filter(Boolean))].sort()
      .map((v) => ({ value: v, label: t(`registration.${v}`, { defaultValue: v }) }));
    const teams = showTeam ? [...new Set(shooters.map((s) => s.team).filter(Boolean))]
      .map((id) => {
        const team = (match.teams || []).find((te) => te.id === id);
        return { value: id, label: team ? team.title : id };
      }).sort((a, b) => a.label.localeCompare(b.label)) : [];
    const icsValues = showIcs ? [...new Set(shooters.map((s) => s.ics).filter(Boolean))].sort()
      .map((v) => ({ value: v, label: t(`classifications.${v.replace(/-([a-z])/g, (_, c) => c.toUpperCase())}Short`, { defaultValue: v }) })) : [];
    return { squads, divisions, categories, powerFactors, teams, icsValues };
  }, [shooters, match?.squads, match?.teams]);

  const sortedShooters = useMemo(() => {
    if (!shooters) return [];
    let list = [...shooters];

    // Text search on name/id
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      list = list.filter((s) =>
        (s.name || '').toLowerCase().includes(q)
        || (s.publicId || '').toString().toLowerCase().includes(q)
      );
    }

    // Apply column filters (Set-based)
    for (const [field, selected] of Object.entries(filters)) {
      if (selected instanceof Set) {
        list = list.filter((s) => selected.has(s[field]));
      }
    }

    return orderBy(list, [(s) => {
      if (sortField === 'squad') {
        const squad = s.squad ? (match.squads || []).find((sq) => sq.id === s.squad) : null;
        return squad ? squad.title : 'zzz';
      }
      if (sortField === 'publicId') {
        const parts = (s.publicId || '').split('|');
        const numericId = parseInt(parts[parts.length - 1], 10);
        return isNaN(numericId) ? s.publicId : numericId;
      }
      return (s[sortField] || '').toString().toLowerCase();
    }], [sortDirection]);
  }, [shooters, sortField, sortDirection, filters, searchText, match?.squads]);

  if (!shooters) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  const FlagId = ({ publicId }) => {
    const parts = (publicId || '').split('|');
    const displayId = parts[parts.length - 1];
    const countryCode = parts.length > 1 ? parts[0] : null;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {countryCode && (
          <img
            alt={countryCode}
            src={getFlagSrc({ countryCode, size: 16 })}
            style={{ display: 'block' }}
          />
        )}
        <span>{displayId}</span>
      </Box>
    );
  };

  const filterProps = { filters, setFilters };

  return (
    <Box sx={{ px: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <TextField
          size="small"
          placeholder={t('common.search')}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
          sx={{ width: 250 }}
        />
      </Box>

      <Box sx={{ maxWidth: '100%', overflowX: 'auto' }}>
        <Table size="small" sx={{ mb: 3, '& th, & td': { textAlign: 'start' } }}>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel active={sortField === 'publicId'} direction={sortDirection} onClick={() => handleSort('publicId')}>
                  {t('shooter.id')}
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel active={sortField === 'name'} direction={sortDirection} onClick={() => handleSort('name')}>
                  {t('shooter.name')}
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TableSortLabel active={sortField === 'club'} direction={sortDirection} onClick={() => handleSort('club')}>
                    {t('shooter.club')}
                  </TableSortLabel>
                  <ColumnFilter field="club" options={[...new Set(shooters.map((s) => s.club).filter(Boolean))].sort().map((v) => ({ value: v, label: v }))} {...filterProps} />
                </Box>
              </TableCell>
              {showTeam && (
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <TableSortLabel active={sortField === 'team'} direction={sortDirection} onClick={() => handleSort('team')}>
                      {t('shooter.team')}
                    </TableSortLabel>
                    <ColumnFilter field="team" options={uniqueValues.teams} {...filterProps} />
                  </Box>
                </TableCell>
              )}
              {showIcs && (
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <TableSortLabel active={sortField === 'ics'} direction={sortDirection} onClick={() => handleSort('ics')}>
                      {t('registration.icsShort')}
                    </TableSortLabel>
                    <ColumnFilter field="ics" options={uniqueValues.icsValues} {...filterProps} />
                  </Box>
                </TableCell>
              )}
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TableSortLabel active={sortField === 'squad'} direction={sortDirection} onClick={() => handleSort('squad')}>
                    {t('results.squadNumber')}
                  </TableSortLabel>
                  <ColumnFilter field="squad" options={uniqueValues.squads} {...filterProps} />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TableSortLabel active={sortField === 'division'} direction={sortDirection} onClick={() => handleSort('division')}>
                    {t('shooter.division')}
                  </TableSortLabel>
                  <ColumnFilter field="division" options={uniqueValues.divisions} {...filterProps} />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TableSortLabel active={sortField === 'category'} direction={sortDirection} onClick={() => handleSort('category')}>
                    {t('shooter.category')}
                  </TableSortLabel>
                  <ColumnFilter field="category" options={uniqueValues.categories} {...filterProps} />
                </Box>
              </TableCell>
              {match.type !== 'ipsc-shotgun' && (
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <TableSortLabel active={sortField === 'power-factor'} direction={sortDirection} onClick={() => handleSort('power-factor')}>
                      {t('shooter.powerFactor')}
                    </TableSortLabel>
                    <ColumnFilter field="power-factor" options={uniqueValues.powerFactors} {...filterProps} />
                  </Box>
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedShooters.map((shooter) => (
              <TableRow
                key={shooter.id}
                hover
                onClick={() => setSelectedShooter(shooter)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>
                  <FlagId publicId={shooter.publicId} />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {shooter.name}
                    {['ro', 'cro', 'so', 'md', 'rm', 'qm'].map((role) =>
                      (shooter.labels || []).includes(role) ? (
                        <Chip key={role} label={role.toUpperCase()} size="small" color="primary" sx={{ borderRadius: 0, height: 'auto', fontSize: 9, ml: 0.5 }} />
                      ) : null
                    )}
                  </Box>
                </TableCell>
                <TableCell>{shooter.club}</TableCell>
                {showTeam && (
                  <TableCell>
                    {shooter.team ? ((match.teams || []).find((team) => team.id === shooter.team) || {}).title || '' : ''}
                  </TableCell>
                )}
                {showIcs && (
                  <TableCell>
                    {shooter.ics ? t(`classifications.${shooter.ics.replace(/-([a-z])/g, (_, c) => c.toUpperCase())}Short`, { defaultValue: shooter.ics }) : ''}
                  </TableCell>
                )}
                <TableCell>{getSquadName(shooter.squad)}</TableCell>
                <TableCell>{t(`divisions.${toDivisionKey(shooter.division)}`, { defaultValue: shooter.division })}</TableCell>
                <TableCell>{t(`categories.${toCategoryKey(shooter.category)}`, { defaultValue: shooter.category })}</TableCell>
                {match.type !== 'ipsc-shotgun' && (
                  <TableCell>{t(`registration.${shooter['power-factor']}`, { defaultValue: shooter['power-factor'] })}</TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      {/* Shooter Detail Dialog */}
      <Dialog open={!!selectedShooter} onClose={() => setSelectedShooter(null)} maxWidth="sm" fullWidth>
        {selectedShooter && (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FlagId publicId={selectedShooter.publicId} />
                <span>- {selectedShooter.name}</span>
              </Box>
              <IconButton onClick={() => setSelectedShooter(null)} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, py: 1 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('shooter.club')}</Typography>
                  <Typography>{selectedShooter.club || '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('shooter.division')}</Typography>
                  <Typography>{t(`divisions.${toDivisionKey(selectedShooter.division)}`, { defaultValue: selectedShooter.division })}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('shooter.category')}</Typography>
                  <Typography>{t(`categories.${toCategoryKey(selectedShooter.category)}`, { defaultValue: selectedShooter.category })}</Typography>
                </Box>
                {match.type !== 'ipsc-shotgun' && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">{t('shooter.powerFactor')}</Typography>
                    <Typography>{t(`registration.${selectedShooter['power-factor']}`, { defaultValue: selectedShooter['power-factor'] })}</Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('results.squadNumber')}</Typography>
                  <Typography>{getSquadName(selectedShooter.squad) || '-'}</Typography>
                </Box>
                {showTeam && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">{t('shooter.team')}</Typography>
                    <Typography>
                      {selectedShooter.team
                        ? ((match.teams || []).find((team) => team.id === selectedShooter.team) || {}).title || '-'
                        : '-'}
                    </Typography>
                  </Box>
                )}
                {showIcs && selectedShooter.ics && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">{t('registration.ics')}</Typography>
                    <Typography>{t(`classifications.${selectedShooter.ics.replace(/-([a-z])/g, (_, c) => c.toUpperCase())}`, { defaultValue: selectedShooter.ics })}</Typography>
                  </Box>
                )}
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
}
